/** The board's L0 strip: the four numbers leadership is already given.
 *
 *  These are not four numbers chosen here — they are the four the quarterly
 *  "IT & OT Security" deck already puts in front of the leadership team, in the
 *  deck's own words. "Dangerous emails blocked before delivery", not
 *  `messagesBlocked`; "incidents with business impact", not `reached_user`. A
 *  reader who has seen the slide recognises the tile, and the tile is the
 *  recomputable version of it.
 *
 *  Four `Statistic` tiles, not charts (dashboard design rules §2 — one current
 *  value with a trend is a Statistic with a MiniSparkline, never a one-bar
 *  chart). The pairing that matters is the first and the third: "blocked" is
 *  reassuring on its own, so it sits near "incidents with business impact",
 *  which is danger-toned the moment it leaves zero. Zero is the normal state
 *  there, and the deck says so; a tile that went green on zero would make the
 *  abnormal state look like an improvement.
 *
 *  Every tile is a door (§4.1): each is an anchor into the L1 panel that
 *  explains it, so the target lands in the URL and the back button works.
 */

import { Card, MiniSparkline, Statistic, Typography } from "@norican/react";
import type { ReactNode } from "react";

import type { SecuritySummary, SecurityTrendPoint } from "@/lib/api";

import {
  blockRateTone,
  coverageTone,
  formatCount,
  formatPct,
  formatRate,
  reachedUserTone,
} from "./status";

/** Anchor ids the tiles link to — the board's own L1 panels. */
export const TREND_ANCHOR = "security-blocked-trend";
export const MIX_ANCHOR = "security-classification-mix";
export const RATE_ANCHOR = "security-block-rate";
export const CAMPAIGN_ANCHOR = "security-campaigns";
export const TARGETED_ANCHOR = "security-targeted-people";

interface ExecTilesProps {
  summary: SecuritySummary;
  trend: SecurityTrendPoint[];
  /** The pinned definition sentence, rendered under the headline tiles. */
  definition: string;
}

export function ExecTiles({ summary, trend, definition }: ExecTilesProps) {
  const spark = trend.map((point) => point.blocked);
  const impact = summary.reached_user;

  return (
    <section aria-label="Headline security figures" className="flex flex-col gap-3">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <TileLink anchor={TREND_ANCHOR} label="Blocked volume trend">
          <Statistic
            label="Dangerous emails blocked before delivery"
            value={formatCount(summary.blocked)}
            tone="success"
            size="lg"
            accent
            caption={`Last ${summary.window_days} days · ${formatCount(summary.recipients)} recipients addressed`}
            delta={
              spark.length > 1 ? (
                <MiniSparkline
                  data={spark}
                  tone="success"
                  width={72}
                  height={20}
                  endMarker="dot"
                  aria-hidden
                />
              ) : undefined
            }
          />
        </TileLink>

        <TileLink anchor={TARGETED_ANCHOR} label="People individually targeted">
          <Statistic
            label="People individually targeted"
            value={formatCount(summary.targeted_people)}
            tone={summary.targeted_people ? "warning" : "neutral"}
            size="lg"
            accent
            caption={
              summary.targeted_people === null || summary.targeted_people === undefined
                ? "The daily People batch has not run yet"
                : "Named below for the security role; a count for everyone else"
            }
          />
        </TileLink>

        {/* The tile that keeps the headline honest. Danger the moment it leaves
            zero — a payload that reached a mailbox is a failed control, not a
            degraded one — and never green, because the deck's claim is that
            zero is the normal state rather than an achievement. */}
        <TileLink anchor={RATE_ANCHOR} label="Incidents with business impact">
          <Statistic
            label="Incidents with business impact"
            value={formatCount(impact)}
            tone={reachedUserTone(impact)}
            size="lg"
            accent
            caption={
              impact === 0
                ? "Zero is the normal state: nothing dangerous was delivered and no malicious link was clicked"
                : `${formatCount(summary.delivered)} dangerous messages delivered · ${formatCount(
                    summary.clicks_permitted,
                  )} malicious clicks permitted`
            }
          />
        </TileLink>

        <TileLink anchor={RATE_ANCHOR} label="Block rate and its coverage">
          <Statistic
            label="Block rate"
            value={formatRate(summary.block_rate_pct)}
            tone={blockRateTone(summary.block_rate_pct)}
            size="lg"
            accent
            progress={summary.block_rate_pct ?? undefined}
            progressTone={
              blockRateTone(summary.block_rate_pct) === "success" ? "success" : "warning"
            }
            caption={
              <span
                className={coverageTone(summary.coverage_pct) === "danger" ? "text-danger" : ""}
              >
                {summary.days_with_data} of {summary.window_days} days collected (
                {formatPct(summary.coverage_pct)} coverage)
              </span>
            }
          />
        </TileLink>
      </div>

      {/* The definition sits with the number, not in a footnote in another
          document. The first time someone asks "17,500 of what?", the answer
          is already on the screen. */}
      <Typography variant="body-sm" tone="muted">
        {definition}
      </Typography>

      {summary.actors.length > 0 && (
        <a
          href={`#${CAMPAIGN_ANCHOR}`}
          className="text-fg-muted hover:text-fg text-sm underline underline-offset-2"
        >
          {summary.campaigns} attributed campaign{summary.campaigns === 1 ? "" : "s"} ·{" "}
          {summary.actors.slice(0, 4).join(", ")}
          {summary.actors.length > 4 ? ` +${summary.actors.length - 4} more` : ""}
        </a>
      )}
    </section>
  );
}

interface TileLinkProps {
  /** The L1 panel this tile explains. */
  anchor: string;
  /** Accessible name for the link — the tile's own value is inside the card. */
  label: string;
  children: ReactNode;
}

/** A KPI tile that is also a door — no dead aggregates (§4.1). */
function TileLink({ anchor, label, children }: TileLinkProps) {
  return (
    <a href={`#${anchor}`} aria-label={label} className="block">
      <Card variant="outline" className="hover:border-accent h-full transition-colors">
        <Card.Body>{children}</Card.Body>
      </Card>
    </a>
  );
}
